import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchOrderbook,
  fetchTrades,
  fetchOpenOrders,
  submitFuturesOrder,
  fetchTickers,
} from '../api.js';

const formatNumber = (value, opts = {}) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: opts.minimumFractionDigits ?? 2,
    maximumFractionDigits: opts.maximumFractionDigits ?? 2,
  }).format(Number(value));
};

export default function PairDetailView() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [orderbook, setOrderbook] = useState({ asks: [], bids: [] });
  const [trades, setTrades] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [tickerInfo, setTickerInfo] = useState(null);
  const [side, setSide] = useState('buy');
  const [orderType, setOrderType] = useState('limit');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const intervalRef = useRef(null);
  const chartRef = useRef(null);

  const baseAsset = symbol.replace(/USDT|BTC|ETH|BNB/g, '');
  const quoteAsset = symbol.replace(baseAsset, '');

  const refreshData = useCallback(async () => {
    try {
      const [ob, tr, oo, tickers] = await Promise.all([
        fetchOrderbook(symbol),
        fetchTrades(symbol),
        fetchOpenOrders(symbol),
        fetchTickers(),
      ]);
      setOrderbook(ob || { asks: [], bids: [] });
      setTrades(tr || []);
      setOpenOrders(oo || []);
      const allTickers = Object.values(tickers.tickers_by_quote || {}).flat();
      setTickerInfo(allTickers.find((t) => t.symbol === symbol) || null);
    } catch (err) {
      console.error('Erro ao atualizar dados:', err);
    }
  }, [symbol]);

  useEffect(() => {
    refreshData();
    intervalRef.current = setInterval(refreshData, 5000);
    return () => clearInterval(intervalRef.current);
  }, [refreshData]);

  // Update page title with symbol price
  useEffect(() => {
    if (tickerInfo?.price) {
      const price = formatNumber(tickerInfo.price, { maximumFractionDigits: 2 });
      const chg = tickerInfo.change_percent !== null ? Number(tickerInfo.change_percent) : null;
      if (chg !== null) {
        const emoji = chg >= 0 ? '�' : '�';
        document.title = `${emoji} ${symbol} ${price} (${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%)`;
      } else {
        document.title = `${symbol} ${price}`;
      }
    } else {
      document.title = symbol;
    }
    return () => { document.title = 'Mid Trader'; };
  }, [tickerInfo?.price, tickerInfo?.change_percent, symbol]);

  // TradingView chart
  useEffect(() => {
    if (chartRef.current && window.TradingView) {
      chartRef.current.innerHTML = '';
      new window.TradingView.widget({
        autosize: true,
        symbol: `BINANCE:${symbol}`,
        interval: '1D',
        timezone: 'Etc/UTC',
        theme: 'dark',
        style: '1',
        locale: 'br',
        toolbar_bg: '#131722',
        enable_publishing: false,
        hide_legend: false,
        container_id: 'tv-chart',
        studies: ['Volume@tv-basicstudies'],
      });
    }
  }, [symbol]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quantity) {
      alert('Informe a quantidade.');
      return;
    }
    const payload = {
      side: side.toUpperCase(),
      type: orderType.toUpperCase(),
      quantity,
      timeInForce: 'GTC',
    };
    if (orderType !== 'market' && price) {
      payload.price = price;
    }
    try {
      await submitFuturesOrder(symbol, payload);
      alert('Ordem enviada com sucesso!');
      setPrice('');
      setQuantity('');
      await refreshData();
    } catch (err) {
      alert(`Erro ao enviar ordem: ${err.message}`);
    }
  };

  const topAsks = (orderbook.asks || []).slice(0, 10).reverse();
  const topBids = (orderbook.bids || []).slice(0, 10);
  const spreadPrice = topBids.length > 0 ? topBids[0][0] : '—';

  return (
    <div className="trading-interface">
      <header className="trading-header">
        <div className="trading-header-left">
          <button className="back-button" onClick={() => navigate('/')}>←</button>
          <div className="pair-info">
            <div className="pair-symbol">{symbol}</div>
            <div className="pair-details">
              <span className="pair-price">
                {tickerInfo ? formatNumber(tickerInfo.price, { maximumFractionDigits: 2 }) : 'Carregando...'}
              </span>
              <span className={`pair-change ${tickerInfo && Number(tickerInfo.change_percent) >= 0 ? 'positive' : 'negative'}`}>
                {tickerInfo && tickerInfo.change_percent !== null
                  ? `${Number(tickerInfo.change_percent) >= 0 ? '+' : ''}${formatNumber(tickerInfo.change_percent, { maximumFractionDigits: 2 })}%`
                  : '--'}
              </span>
            </div>
          </div>
        </div>
        <div className="trading-header-center">
          <div className="market-stats">
            <div className="stat-item">
              <span className="stat-label">Volume 24h</span>
              <span className="stat-value">{tickerInfo ? tickerInfo.quote_volume_display : '--'}</span>
            </div>
          </div>
        </div>
        <div className="trading-header-right">
          <button className="header-button">Carteira Spot</button>
          <button className="header-button">Cruzada</button>
          <button className="header-button">Isolada</button>
        </div>
      </header>

      <div className="trading-layout">
        <div className="chart-section">
          <div className="chart-controls">
            <div className="timeframe-buttons">
              <button className="timeframe-btn active">1D</button>
            </div>
            <div className="chart-tools">
              <button className="chart-tool">Trading View</button>
            </div>
          </div>
          <div id="tv-chart" ref={chartRef} className="chart-container"></div>
        </div>

        <div className="trading-sidebar">
          <div className="orderbook-section">
            <div className="section-header">
              <h3>Livro de Ordens</h3>
            </div>
            <div className="orderbook-content">
              <div className="orderbook-header">
                <span>Preço ({quoteAsset})</span>
                <span>Quantidade ({baseAsset})</span>
                <span>Total</span>
              </div>
              <div className="orderbook-asks">
                {topAsks.map(([p, q], i) => (
                  <div key={`a${i}`} className="orderbook-row ask-row">
                    <span className="price ask-price">{formatNumber(p, { maximumFractionDigits: 2 })}</span>
                    <span className="quantity">{formatNumber(q, { maximumFractionDigits: 6 })}</span>
                    <span className="total">{formatNumber(p * q, { maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
              <div className="orderbook-spread">
                <span className="spread-price">{formatNumber(spreadPrice, { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="orderbook-bids">
                {topBids.map(([p, q], i) => (
                  <div key={`b${i}`} className="orderbook-row bid-row">
                    <span className="price bid-price">{formatNumber(p, { maximumFractionDigits: 2 })}</span>
                    <span className="quantity">{formatNumber(q, { maximumFractionDigits: 6 })}</span>
                    <span className="total">{formatNumber(p * q, { maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="order-form-section">
            <div className="order-tabs">
              <button className="order-tab active">Spot</button>
            </div>
            <div className="order-form-content">
              <div className="buy-sell-buttons">
                <button
                  className={`buy-button ${side === 'buy' ? 'active' : ''}`}
                  onClick={() => setSide('buy')}
                >
                  Comprar {baseAsset}
                </button>
                <button
                  className={`sell-button ${side === 'sell' ? 'active' : ''}`}
                  onClick={() => setSide('sell')}
                >
                  Vender {baseAsset}
                </button>
              </div>
              <form className="order-form" onSubmit={handleSubmit}>
                <div className="order-type-tabs">
                  <button
                    type="button"
                    className={`order-type-tab ${orderType === 'limit' ? 'active' : ''}`}
                    onClick={() => setOrderType('limit')}
                  >
                    Limite
                  </button>
                  <button
                    type="button"
                    className={`order-type-tab ${orderType === 'market' ? 'active' : ''}`}
                    onClick={() => setOrderType('market')}
                  >
                    Market
                  </button>
                </div>
                {orderType !== 'market' && (
                  <div className="form-group">
                    <label>Preço</label>
                    <div className="input-group">
                      <input
                        type="number"
                        placeholder="0.00"
                        step="any"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                      />
                      <span className="input-suffix">{quoteAsset}</span>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label>Quantidade</label>
                  <div className="input-group">
                    <input
                      type="number"
                      placeholder="0.00000000"
                      step="any"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                    <span className="input-suffix">{baseAsset}</span>
                  </div>
                </div>
                <button
                  type="submit"
                  className={`submit-order-btn ${side === 'buy' ? 'buy-order' : 'sell-order'}`}
                >
                  {side === 'buy' ? 'Comprar' : 'Vender'} {baseAsset}
                </button>
              </form>
            </div>
          </div>

          <div className="trades-section">
            <div className="section-header">
              <h3>Trades de Mercado</h3>
            </div>
            <div className="trades-content">
              <div className="trades-header">
                <span>Preço ({quoteAsset})</span>
                <span>Qtd ({baseAsset})</span>
                <span>Hora</span>
              </div>
              <div className="trades-list">
                {trades.slice(0, 20).map((trade, i) => (
                  <div key={i} className={`trade-row ${trade.isBuyerMaker ? 'sell-trade' : 'buy-trade'}`}>
                    <span className="trade-price">{formatNumber(trade.price, { maximumFractionDigits: 2 })}</span>
                    <span className="trade-quantity">{formatNumber(trade.qty, { maximumFractionDigits: 6 })}</span>
                    <span className="trade-time">
                      {new Date(trade.time).toLocaleTimeString('pt-BR', { hour12: false })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bottom-section">
        <div className="bottom-tabs">
          <button className="bottom-tab active">Ordens Abertas ({openOrders.length})</button>
        </div>
        <div className="bottom-content">
          <div className="bottom-panel active">
            <div className="orders-table-container">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Tipo</th><th>Lado</th><th>Preço</th>
                    <th>Quantidade</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openOrders.length === 0 ? (
                    <tr><td colSpan="6" className="no-data">Nenhuma ordem aberta</td></tr>
                  ) : (
                    openOrders.map((order) => (
                      <tr key={order.orderId}>
                        <td>{order.orderId}</td>
                        <td>{order.type}</td>
                        <td className={order.side === 'BUY' ? 'buy' : 'sell'}>
                          {order.side === 'BUY' ? 'Compra' : 'Venda'}
                        </td>
                        <td>{order.price || 'Market'}</td>
                        <td>{order.origQty}</td>
                        <td>{order.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
